const WRAPPED = Symbol('wrapScriptValue')

function wrapString(value: string): unknown {
  const holder = { __str: value }
  return new Proxy(holder, {
    has(target, key) {
      if (key === WRAPPED) return true
      return key in target
    },
    get(target, key) {
      if (key === WRAPPED) return true
      if (key === 'length') return () => target.__str.length
      if (key === 'toString') return () => target.__str
      if (key === Symbol.toPrimitive) {
        return (hint: string) => (hint === 'number' ? Number(target.__str) : target.__str)
      }

      const str = target.__str
      let prop: unknown = (str as any)[key as any]
      if (prop === undefined && typeof key === 'string') {
        prop = (String.prototype as any)[key]
      }
      if (typeof prop === 'function') {
        return (...args: unknown[]) => {
          const result = prop.apply(str, args)
          return typeof result === 'string' ? wrapString(result) : result
        }
      }
      if (prop !== undefined) return prop
      return (target as any)[key]
    },
  })
}

function wrapArray(value: unknown[]): unknown {
  return new Proxy(value, {
    has(target, key) {
      if (key === WRAPPED) return true
      return Reflect.has(target, key)
    },
    get(target, key) {
      if (key === WRAPPED) return true
      if (key === 'size') return () => target.length
      return (target as any)[key]
    },
  })
}

function wrapSizedValue(value: object): unknown {
  return new Proxy(value, {
    has(target, key) {
      if (key === WRAPPED) return true
      return Reflect.has(target, key)
    },
    get(target, key, receiver) {
      if (key === WRAPPED) return true
      if (key === 'length') {
        return () => (target as { size(): number }).size()
      }
      return Reflect.get(target, key, receiver)
    },
  })
}

function wrapTableLike(value: Record<string, unknown>): unknown {
  const rowCount = () => ((value as { rows?: unknown[] }).rows?.length ?? 0)
  return new Proxy(value, {
    has(target, key) {
      if (key === WRAPPED) return true
      return Reflect.has(target, key)
    },
    get(target, key) {
      if (key === WRAPPED) return true
      if (key === 'size' || key === 'length') return () => rowCount()
      return (target as any)[key]
    },
  })
}

export function isScriptWrappedValue(value: unknown): boolean {
  return value != null && typeof value === 'object' && WRAPPED in (value as object)
}

function readPlainStrHolder(value: unknown): string | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null
  const strHolder = (value as { __str?: unknown }).__str
  if (typeof strHolder !== 'string') return null
  if (isScriptWrappedValue(value)) return strHolder
  const keys = Object.keys(value as object)
  if (keys.length === 1 && keys[0] === '__str') return strHolder
  return null
}

/**
 * Снимает обёртку wrapScriptValue перед записью в store / передачей в UI-компоненты.
 * Идемпотентна для уже plain-значений. После JSON Proxy остаётся `{__str}` — тоже снимаем.
 */
export function unwrapScriptValue(value: unknown): unknown {
  if (value == null) return value

  const leftoverStr = readPlainStrHolder(value)
  if (leftoverStr !== null) return leftoverStr

  if (!isScriptWrappedValue(value)) return value

  const strHolder = (value as { __str?: unknown }).__str
  if (typeof strHolder === 'string') {
    return strHolder
  }

  if (Array.isArray(value)) {
    return (value as unknown[]).map((item) => unwrapScriptValue(item))
  }

  if (typeof value === 'object') {
    const rows = (value as { rows?: unknown }).rows
    if (Array.isArray(rows)) {
      const obj = value as Record<string, unknown>
      const plain: Record<string, unknown> = {}
      for (const key of Reflect.ownKeys(obj)) {
        if (typeof key === 'symbol') continue
        plain[String(key)] = unwrapScriptValue(Reflect.get(obj, key, value))
      }
      return plain
    }
  }

  return value
}

/**
 * Оборачивает значение для Rhino/Java-скриптов: .size() у массивов/таблиц, .length() у строк.
 * Не мутирует исходный объект (Vue reactive-safe).
 */
export function wrapScriptValue(value: unknown): unknown {
  if (value == null) return value
  if (typeof value === 'object' && WRAPPED in (value as object)) return value

  if (typeof value === 'string') return wrapString(value)

  if (typeof (value as { size?: unknown }).size === 'function') {
    if (typeof (value as { length?: unknown }).length === 'function') return value
    return wrapSizedValue(value)
  }

  if (Array.isArray(value)) return wrapArray(value)

  if (typeof value === 'object' && Array.isArray((value as { rows?: unknown }).rows)) {
    return wrapTableLike(value as Record<string, unknown>)
  }

  return value
}
